import { useMemo, useCallback } from 'react';
import { gql, useSubscription } from '@apollo/client';

interface StaffMember {
  id: string;
  name: string;
  primarySpecialty?: string;
  title?: string;
  titleAbbr: string;
}

interface Physician {
  id: string;
  name: string;
}

interface Anesthesiologist {
  id: string;
  name: string;
}

interface Room {
  id: string;
  name: string;
  type: string;
}

interface CurrentLocation {
  room: Room;
  arrivedAt: string;
}

interface StaffSubject {
  id: string;
  role: string;
  entityId: string;
  physician?: Physician;
  staffMember?: StaffMember;
  anesthesiologist?: Anesthesiologist;
  currentLocation?: CurrentLocation;
  arrivedOnSite: string | null;
  leftSite: string | null;
}

interface StaffMonitoringData {
  title: string;
  progress: number;
  timeOfDay: string;
  subjects: StaffSubject[];
}

interface StaffMonitoringResponse {
  staffMonitoring: StaffMonitoringData;
}

interface StaffMemberData {
  type: 'staff' | 'physician' | 'anesthesiologist';
  text: string;
  id: string;
  entityId: string;
  name: string;
  title: string;
  fullTitle: string;
  entryTime?: string;
  arrivedOnSite: string | null;
  timeOfDay: string;
}

type StaffByRoom = Record<string, StaffMemberData[]>;

const STAFF_MONITORING_SUBSCRIPTION = gql`
  subscription staffMonitoring($date: Date) {
    staffMonitoring(date: $date) {
      title
      progress
      timeOfDay
      subjects {
        id
        role
        entityId
        physician {
          id
          name
        }
        staffMember {
          id
          name
          primarySpecialty
          title
          titleAbbr
        }
        anesthesiologist {
          id
          name
        }
        currentLocation {
          room {
            id
            name
            type
          }
          arrivedAt
        }
        arrivedOnSite
        leftSite
      }
    }
  }
`;

export const useStaffMonitoring = (showStaffTracking: boolean) => {
  const { data: staffData } = useSubscription<StaffMonitoringResponse>(STAFF_MONITORING_SUBSCRIPTION, {
    skip: !showStaffTracking,
  });

  const staffByRoom = useMemo((): StaffByRoom => {
    if (!showStaffTracking || !staffData?.staffMonitoring?.subjects) return {};

    const timeOfDay = staffData.staffMonitoring.timeOfDay;
    const roomStaffMap: StaffByRoom = {};

    staffData.staffMonitoring.subjects.forEach((subject, index) => {
      const roomId = subject.currentLocation?.room?.id;

      if (!roomId || subject.arrivedOnSite === null || subject.leftSite) return;

      if (!roomStaffMap[roomId]) {
        roomStaffMap[roomId] = [];
      }

      let staffMemberData: StaffMemberData | null = null;

      if (subject.staffMember && subject.staffMember.titleAbbr) {
        staffMemberData = {
          type: 'staff',
          text: subject.staffMember.titleAbbr,
          id: subject.id || `staff-${index}`,
          entityId: subject.entityId,
          name: subject.staffMember.name,
          title: subject.staffMember.titleAbbr,
          fullTitle: subject.staffMember.title || subject.staffMember.titleAbbr,
          entryTime: subject.currentLocation?.arrivedAt,
          arrivedOnSite: subject.arrivedOnSite,
          timeOfDay: timeOfDay,
        };
      } else if (subject.physician) {
        staffMemberData = {
          type: 'physician',
          text: 'PHY',
          id: subject.id || `physician-${index}`,
          entityId: subject.entityId,
          name: subject.physician.name,
          title: 'PHY',
          fullTitle: 'Physician',
          entryTime: subject.currentLocation?.arrivedAt,
          arrivedOnSite: subject.arrivedOnSite,
          timeOfDay: timeOfDay,
        };
      } else if (subject.anesthesiologist) {
        staffMemberData = {
          type: 'anesthesiologist',
          text: 'Anes',
          id: subject.id || `anesthesiologist-${index}`,
          entityId: subject.entityId,
          name: subject.anesthesiologist.name,
          title: 'Anes',
          fullTitle: 'Anesthesiologist',
          entryTime: subject.currentLocation?.arrivedAt,
          arrivedOnSite: subject.arrivedOnSite,
          timeOfDay: timeOfDay,
        };
      }

      if (staffMemberData) {
        roomStaffMap[roomId].push(staffMemberData);
      }
    });

    return roomStaffMap;
  }, [showStaffTracking, staffData?.staffMonitoring?.subjects, staffData?.staffMonitoring?.timeOfDay]);

  const getStaffForRoom = useCallback((roomId: string): StaffMemberData[] => staffByRoom[roomId] || [], [staffByRoom]);

  return { getStaffForRoom };
};
